<script lang="ts" setup>
  import { useDebounceFn, useEventListener, usePointerSwipe } from '@vueuse/core';
  import { computed, onMounted, ref, useTemplateRef } from 'vue';
  import { startUserAction, userAction } from '../../../../shared/observability/userAction.ts';
  import { GameCode } from '../../../../widgets/game-code';
  import { RulesChooser, RulesGuesser, RulesOverview } from '../../../../widgets/rules';

  const props = defineProps<{ gameCode: number; collapseInto?: HTMLElement | null }>();
  const emit = defineEmits<{ close: [] }>();

  const pageComponents = [RulesOverview, RulesChooser, RulesGuesser];
  const pageCount = pageComponents.length;
  const currentPage = ref(0);
  const dialog = useTemplateRef<HTMLDialogElement>('dialog');
  const content = useTemplateRef<HTMLElement>('content');
  const track = useTemplateRef<HTMLElement>('track');
  const closing = ref(false);

  onMounted(() => dialog.value?.showModal());

  const page = () => ({ page: currentPage.value + 1 });

  let pageHeadedFor = 0;

  function settleOn(settled: number) {
    pageHeadedFor = settled;
    if (settled === currentPage.value) return;
    startUserAction(settled > currentPage.value ? userAction.lobbyRulesNext : userAction.lobbyRulesBack, page());
    currentPage.value = settled;
  }

  function settleOnScrolledPage() {
    const scroller = track.value;
    if (scroller) settleOn(Math.round(scroller.scrollLeft / scroller.clientWidth));
  }

  const browserHasScrollEnd = 'onscrollend' in window;
  const scrollStopDelay = 100;

  if (browserHasScrollEnd) useEventListener(track, 'scrollend', settleOnScrolledPage);
  else useEventListener(track, 'scroll', useDebounceFn(settleOnScrolledPage, scrollStopDelay));

  function scrollToPage(index: number) {
    const scroller = track.value;
    scroller?.scrollTo({ left: index * scroller.clientWidth });
  }

  function stepBy(pageCountToStep: number) {
    pageHeadedFor = Math.min(Math.max(pageHeadedFor + pageCountToStep, 0), pageCount - 1);
    scrollToPage(pageHeadedFor);
  }

  const swipeThreshold = 30;
  const dragOffset = ref(0);
  const dragVelocity = ref(0);
  const dismissDragShare = 0.25;
  const dismissVelocity = 0.5;
  const snapBackDuration = 300;
  const snapBackEase = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  let lastDragSample = { time: 0, offset: 0 };

  const { direction: swipeDirection, distanceY } = usePointerSwipe(content, {
    threshold: swipeThreshold,
    disableTextSelect: true,
    onSwipeStart() {
      lastDragSample = { time: performance.now(), offset: 0 };
      dragVelocity.value = 0;
    },
    onSwipe() {
      if (closing.value || !isVerticalSwipe() || !currentPageScrolledToEnd()) return;

      const offset = Math.max(0, distanceY.value - swipeThreshold);
      const now = performance.now();
      dragVelocity.value = (offset - lastDragSample.offset) / Math.max(1, now - lastDragSample.time);
      lastDragSample = { time: now, offset };
      dragOffset.value = offset;
    },
    onSwipeEnd() {
      if (closing.value || dragOffset.value === 0) return;

      const draggedFarEnough = dragOffset.value > sheetHeight() * dismissDragShare;
      const flungUp = dragVelocity.value > dismissVelocity;
      if (draggedFarEnough || flungUp) close();
      else snapBack();
    },
  });

  const isVerticalSwipe = () => swipeDirection.value === 'up' || swipeDirection.value === 'down';
  const sheetHeight = () => content.value?.offsetHeight ?? 0;

  const dragProgress = computed(() => Math.min(1, dragOffset.value / (sheetHeight() * dismissDragShare || 1)));
  const sheetStyle = computed(() => ({ transform: `translateY(${-dragOffset.value}px)` }));
  const backdropStyle = computed(() => ({ opacity: 1 - dragProgress.value * 0.5 }));

  async function snapBack() {
    const sheet = content.value;
    const lifted = dragOffset.value;
    dragOffset.value = 0;
    if (!sheet) return;

    await sheet.animate([{ transform: `translateY(${-lifted}px)` }, { transform: 'translateY(0)' }], {
      duration: snapBackDuration,
      easing: snapBackEase,
    }).finished;
  }

  function currentPageScrolledToEnd() {
    const shown = track.value?.children[currentPage.value];
    return !shown || shown.scrollTop + shown.clientHeight >= shown.scrollHeight - 1;
  }

  const collapseDuration = 450;
  const crossfadeStart = 0.66;
  const iosEase = 'cubic-bezier(0.32, 0.72, 0, 1)';

  let reopenedForCollapse = false;

  function leaveTheModalLayer() {
    reopenedForCollapse = true;
    dialog.value?.close();
    dialog.value?.show();
  }

  function emitCloseOnceCollapsed() {
    if (reopenedForCollapse) reopenedForCollapse = false;
    else emit('close');
  }

  async function close() {
    if (closing.value) return;
    startUserAction(userAction.lobbyRulesClose, page());
    closing.value = true;
    leaveTheModalLayer();
    await collapseIntoAnchor();
    dialog.value?.close();
  }

  function zoomKeyframes(sheet: HTMLElement, to: DOMRect) {
    const from = sheet.getBoundingClientRect();
    const scale = to.width / from.width;
    const lifted = dragOffset.value;
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2) - lifted;
    const cropToSquare = (from.height - from.width) / 2;
    const circle = `inset(${cropToSquare}px 0 round ${from.width / 2}px)`;
    const rounded = `inset(0 round ${getComputedStyle(sheet).borderRadius})`;
    const zoomed = `translate(${dx}px, ${dy}px) scale(${scale})`;

    return [
      { clipPath: rounded, transform: `translate(0, ${-lifted}px) scale(1)` },
      { clipPath: circle, transform: zoomed },
    ];
  }

  async function collapseIntoAnchor() {
    const anchor = props.collapseInto;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!anchor || !content.value || !dialog.value || reducedMotion) return;

    const to = anchor.getBoundingClientRect();
    const timing = { duration: collapseDuration, easing: iosEase, fill: 'forwards' as const };

    const crossfade = [{ opacity: 1 }, { opacity: 1, offset: crossfadeStart }, { opacity: 0 }];

    const collapse = content.value.animate(zoomKeyframes(content.value, to), timing);
    content.value.animate(crossfade, { ...timing, easing: 'linear' });
    dialog.value.animate([{ opacity: 1 }, { opacity: 0 }], timing);

    await collapse.finished;
  }
</script>

<template>
  <dialog
    ref="dialog"
    :data-closing="closing"
    :style="backdropStyle"
    aria-label="Rules"
    @cancel.prevent="close"
    @click.self="close"
    @close="emitCloseOnceCollapsed"
  >
    <div ref="content" :style="sheetStyle" class="sheet">
      <div ref="track" class="pages">
        <section
          v-for="(pageComponent, index) in pageComponents"
          :key="index"
          :aria-label="`Page ${index + 1} of ${pageCount}`"
          class="page"
          tabindex="0"
        >
          <component :is="pageComponent" />
        </section>
      </div>

      <div aria-hidden="true" class="dots">
        <span v-for="i in pageCount" :key="i" :data-current="currentPage === i - 1" class="dot" />
      </div>

      <div class="nav">
        <button v-if="currentPage > 0" class="nav-button" type="button" @click="stepBy(-1)">Back</button>
        <GameCode v-else :game-code="gameCode" class="nav-game-code" />
        <button v-if="currentPage < pageCount - 1" class="nav-button primary" type="button" @click="stepBy(1)">
          Next
        </button>
        <button v-else class="nav-button primary" type="button" @click="close">Got it!</button>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
  dialog {
    background: var(--color-backdrop);
    border: none;
    color: inherit;
    height: 100dvh;
    margin: 0;
    max-height: none;
    max-width: none;
    padding: 1rem var(--view-gutter) var(--modal-bottom-inset);
    width: 100%;

    &[open] {
      align-items: flex-end;
      display: flex;
      justify-content: center;
    }

    &::backdrop {
      background: transparent;
    }

    &[data-closing='true'] {
      inset: 0;
      pointer-events: none;
      position: fixed;
      z-index: 10;
    }
  }

  .sheet {
    background: var(--color-surface);
    border-radius: var(--radius-card);
    box-shadow: 0 10px 25px var(--color-shadow);
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    height: var(--modal-height);
    max-width: var(--modal-max-width);
    padding: 1.5rem 2rem;
    transform-origin: center;
    width: 100%;
  }

  .pages {
    display: grid;
    flex: 1;
    grid-auto-columns: 100%;
    grid-auto-flow: column;
    min-height: 0;
    overflow: auto hidden;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;

    @media (prefers-reduced-motion: no-preference) {
      scroll-behavior: smooth;
    }
  }

  .page {
    overflow-y: auto;
    scroll-snap-align: start;
    scroll-snap-stop: always;

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: -2px;
    }
  }

  .nav-game-code {
    --game-code-label-margin: 0;
    --game-code-label-size: var(--font-size-xs);
    --game-code-value-size: var(--font-size-xl);

    text-align: left;
    width: auto;
  }

  .dots {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  }

  .dot {
    background: var(--color-indicator);
    border-radius: var(--radius-full);
    height: 0.5rem;
    transition: background 0.2s;
    width: 0.5rem;

    &[data-current='true'] {
      background: var(--color-primary);
    }
  }

  .nav {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .nav-button {
    background: var(--color-button-subtle);
    border: none;
    border-radius: var(--radius-control);
    color: var(--color-text-secondary);
    cursor: pointer;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-strong);
    min-height: var(--size-touch-target);
    padding: 0.75rem 1.5rem;
    transition: all 0.2s;

    &:active {
      transform: scale(0.95);
    }

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }
  }

  .nav-button.primary {
    background: var(--color-primary);
    color: var(--color-on-primary);
  }
</style>
